import React from 'react';
import { notifications, extractApiErrorMessage } from '../../utils';
import { Title, Loader, AppTypeSelect, ClearFilters, FarmSelectModal, TablePaginationWrapper } from '../../elements';
import useAuthContext from '../../hooks/useAuthContext';
import Grid from '@material-ui/core/Grid';
import {
  Icon,
  Tooltip,
  IconButton,
  Button,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Checkbox,
  Collapse,
  Typography,
  Divider
} from '@material-ui/core';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { JSONTree } from 'react-json-tree';
import { colors } from '../../utils';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppEventLogApplicationType, MobileInfoType } from '../../models/settings';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import {
  HealthSystemDALModelLogAppEventLog,
  HealthSystemDALModelLogAppEventLogApplicationType,
  SettingsSearchEventLogsParams,
  HealthSystemDomainFarmsResultFarmViewModel
} from '../../api/HealthSystemApi';

const FarmStaff: React.FC<any> = () => {
  const classes = useStyles();
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [appType, setAppType] = React.useState<HealthSystemDALModelLogAppEventLogApplicationType | null>(null);
  const [errorOnly, setErrorOnly] = React.useState(false);
  const [notAnalyzedOnly, setNotAnalyzedOnly] = React.useState(false);
  const [selectedFarm, setSelectedFarm] = React.useState<HealthSystemDomainFarmsResultFarmViewModel | null>(null);
  const [logs, setLogs] = React.useState<HealthSystemDALModelLogAppEventLog[]>([]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLogs([]);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadFarmLogs = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;
    const command = {
      Page: searchPage,
      Size: rowsPerPage,
      FarmId: selectedFarm?.id
    } as SettingsSearchEventLogsParams;

    if (appType !== null) {
      command.Type = appType;
    }

    if (errorOnly) {
      command.ErrorOnly = errorOnly;
    }

    if (notAnalyzedOnly) {
      command.NotAnalyzedOnly = notAnalyzedOnly;
    }

    apiService.api.settings
      .settingsSearchEventLogs(command)
      .then((result) => {
        setLogs(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmLogs();

      loadData();
    }
  }, [loaded]);

  const clearFilters = () => {
    setAppType(null);
    setErrorOnly(false);
    setNotAnalyzedOnly(false);
    setSelectedFarm(null);
    setPage(0);
  };

  const handleSelectedFarmChange = (farm: HealthSystemDomainFarmsResultFarmViewModel | null) => {
    setSelectedFarm(farm);
    setPage(0);
  };

  const handleAppTypeChange = (appType: number) => {
    setPage(0);

    if (appType === 0) {
      setAppType(null);
    } else {
      setAppType(appType as HealthSystemDALModelLogAppEventLogApplicationType);
    }
  };

  const handleErrorsOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorOnly(event.target.checked);
    setPage(0);
  };

  const handleNotAnalyzedOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotAnalyzedOnly(event.target.checked);
    setPage(0);
  };

  function EventLogTableRow(props: { eventLog: HealthSystemDALModelLogAppEventLog }) {
    const { eventLog: log } = props;
    const [rowExpanded, setRowExpanded] = React.useState(false);

    return (
      <React.Fragment key={log.id}>
        <TableRow key={`btn${log.id}`} hover selected={rowExpanded}>
          <TableCell align="center">
            <Tooltip arrow title={rowExpanded ? t('f-logs-hide') : t('f-logs-expand')}>
              <IconButton aria-label="expand row" size="small" onClick={() => setRowExpanded(!rowExpanded)}>
                {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell>{new Date(log.dateTime).toLocaleString()}</TableCell>
          <TableCell>{AppEventLogApplicationType[log.application.type]}</TableCell>
          <TableCell>{log.userInfo?.user?.name}</TableCell>
          <TableCell>{log.application?.version}</TableCell>
          <TableCell>{log.payload?.action}</TableCell>
          <TableCell>
            {log.payload?.isError && (
              <Tooltip arrow title={t('f-logs-error')}>
                <Icon color="error">{'error_outline'}</Icon>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        <TableRow key={`row${log.id}`}>
          <TableCell className={classes.tableRowArrowCell} colSpan={11}>
            <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
              {renderEventLogDetailsSection(log)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const renderAdditionalInfo = (log: HealthSystemDALModelLogAppEventLog) => {
    try {
      const additionalInfo = JSON.parse(log.payload?.additionalInfo || '{}');
      return <JSONTree data={additionalInfo} theme={colors.jsonViewTheme} />;
    } catch {
      return <Typography variant="body2">{log.payload?.additionalInfo}</Typography>;
    }
  };

  const renderEventLogDetailsSection = (log: HealthSystemDALModelLogAppEventLog) => {
    return (
      <Grid container className={classes.breakWord}>
        <Grid item lg={4} md={6} sm={12}>
          <Typography variant="h5">{t('f-user')}</Typography>
          <Divider />
          <Typography variant="subtitle2">{t('f-usr-name')}:</Typography>
          <Typography variant="body1">{log.userInfo?.user?.name}</Typography>
          <Typography variant="subtitle2">{t('f-usr-role')}:</Typography>
          <Typography variant="body1">{log.userInfo?.user?.role}</Typography>
          <Typography variant="subtitle2">{'Farm Id'}:</Typography>
          <Typography variant="body1">{log.userInfo?.user?.farmId}</Typography>
          <Typography variant="subtitle2">{'Veterinary Id'}:</Typography>
          <Typography variant="body1">{log.userInfo?.user?.veterinaryId}</Typography>
          <Typography variant="subtitle2">{'User Id'}:</Typography>
          <Typography variant="body1">{log.userInfo?.user?.userId}</Typography>
          <Typography variant="subtitle2">{t('f-logs-ip')}:</Typography>
          <Typography variant="body1">{log.userInfo?.requestIpAddress}</Typography>
        </Grid>

        {log.userInfo.injector && (
          <Grid item lg={4} md={6} sm={12}>
            <Typography variant="h5">{t('f-settings-injector')}</Typography>
            <Divider />
            <Typography variant="subtitle2">{t('f-com-name')}:</Typography>
            <Typography variant="body1">{log.userInfo?.injector?.name}</Typography>
            <Typography variant="subtitle2">{t('f-logs-mac')}:</Typography>
            <Typography variant="body1">{log.userInfo?.injector?.macAddress}</Typography>
            <Typography variant="subtitle2">{t('m-firmware-controller-application')}:</Typography>
            <Typography variant="body1">{log.userInfo?.injector?.application}</Typography>
            <Typography variant="subtitle2">{t('m-firmware-controller-bootloader')}:</Typography>
            <Typography variant="body1">{log.userInfo?.injector?.bootloader}</Typography>
            <Typography variant="subtitle2">{t('m-firmware-controller-bluetooth')}:</Typography>
            <Typography variant="body1">{log.userInfo?.injector?.bluetooth}</Typography>
            {log.healthInjectorId && (
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<Icon>visibility</Icon>}
                onClick={() => navigate(`/devices/injectors/${log.healthInjectorId}`)}
                style={{ marginTop: 8 }}
              >
                {t('f-com-view-details')}
              </Button>
            )}
          </Grid>
        )}

        {log.userInfo.mobile && (
          <Grid item lg={4} md={6} sm={12}>
            <Typography variant="h5">{t('f-settings-mobile')}</Typography>
            <Divider />
            <Typography variant="subtitle2">{t('f-settings-mobile-type')}:</Typography>
            <Typography variant="body1">{MobileInfoType[log.userInfo?.mobile?.type]}</Typography>
            <Typography variant="subtitle2">{t('f-settings-mobile-model')}::</Typography>
            <Typography variant="body1">{log.userInfo?.mobile?.hardwareModel}</Typography>
            <Typography variant="subtitle2">{t('f-settings-mobile-os')}</Typography>
            <Typography variant="body1">{log.userInfo?.mobile?.operatingSystemVersion}</Typography>
          </Grid>
        )}

        <Grid item lg={4} md={6} sm={12}>
          <Typography variant="h5">{t('f-logs-app-info')}</Typography>
          <Divider />
          <Typography variant="subtitle2">{t('f-com-apptype')}:</Typography>
          <Typography variant="body1">{AppEventLogApplicationType[log.application?.type]}</Typography>
          <Typography variant="subtitle2">{t('f-device-ver')}:</Typography>
          <Typography variant="body1">{log.application?.version}</Typography>
          <Typography variant="subtitle2">{t('f-usr-lang')}:</Typography>
          <Typography variant="body1">{log.application?.languageIso}</Typography>
        </Grid>

        <Grid item lg={6} md={6} sm={12}>
          <Typography variant="h5">{t('f-logs-payload')}</Typography>
          <Divider />
          <Typography variant="subtitle2">{t('f-com-action')}:</Typography>
          <Typography variant="body1">{log.payload?.action}</Typography>
          <Typography variant="subtitle2">{t('f-logs-msg')}:</Typography>
          <Typography variant="body1" display="block">
            {log.payload?.message}
          </Typography>
          <Typography variant="subtitle2">{t('f-logs-stack')}:</Typography>
          <Typography variant="body2">{log.payload?.stackTrace}</Typography>
          <Typography variant="subtitle2">{t('f-logs-add-info')}:</Typography>
          {renderAdditionalInfo(log)}
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item container xs={12} spacing={1}>
        <Grid item>
          <Icon color="primary" fontSize="large">
            {'receipt_long'}
          </Icon>
        </Grid>
        <Grid item>
          <Title>{t('f-com-farm-logs')}</Title>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item container md={12} spacing={1} className={classes.filtersRow}>
            <Grid item md={2} xs={8}>
              <AppTypeSelect value={appType ?? 0} onChange={handleAppTypeChange} allowAny fullWidth />
            </Grid>

            <Grid item>
              <FarmSelectModal
                farm={selectedFarm}
                onChange={handleSelectedFarmChange}
                disabled={state.role !== UserRole.admin}
                sx={{ m: 1 }}
              />
            </Grid>

            <Grid item>
              <FormControlLabel
                control={<Checkbox color="primary" checked={errorOnly} onChange={handleErrorsOnlyChange} />}
                label={t('f-logs-error-only')}
              />
            </Grid>

            <Grid item>
              <FormControlLabel
                control={<Checkbox color="primary" checked={notAnalyzedOnly} onChange={handleNotAnalyzedOnlyChange} />}
                label={t('f-logs-not-analyzed-only')}
              />
            </Grid>
            <Grid item>
              <ClearFilters onClick={clearFilters} />
            </Grid>

            <Grid item>
              <Button
                id="searchBtn"
                variant="contained"
                color="primary"
                size="small"
                className={classes.formControl}
                onClick={() => setLoaded(false)}
                startIcon={<Icon>{'search'}</Icon>}
              >
                {t('f-search')}
              </Button>
            </Grid>
          </Grid>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <Grid item md={12}>
              <TablePaginationWrapper
                rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              >
                <Paper elevation={1}>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>{''}</TableCell>
                        <TableCell>{t('f-com-date-time')}</TableCell>
                        <TableCell>{t('f-com-apptype')}</TableCell>
                        <TableCell>{t('f-user')}</TableCell>
                        <TableCell>{t('f-device-ver')}</TableCell>
                        <TableCell>{t('f-com-action')}</TableCell>
                        <TableCell>{''}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {logs.map((log, index) => (
                        <EventLogTableRow key={index} eventLog={log} />
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FarmStaff;
